import { FC } from 'react';

import { FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useField } from 'formik';

import { Flex } from 'src/components/Flex';
import { NPICurrencyInputFieldLabelled } from 'src/components/FormikComponents/NPICurrencyInputField/NPICurrencyInputFieldLabelled';

interface TwoToggleRatesProps {
    isOdd?: boolean;
    namePrefix: string;
    heading: string;
    isConstant?: boolean;
    hardCodedToggle?: boolean;
}

export const TWO_TOGGLE_PRICING_TYPES = [
    { label: 'Per Item', value: 'PER_ITEM' },
    { label: 'Fixed', value: 'FIXED' },
];

const PricingTypeToggle: FC<{
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
}> = ({ value, disabled, onChange }) => {
    return (
        <RadioGroup
            row
            onChange={(e) => {
                return onChange(e.target.value);
            }}
        >
            {TWO_TOGGLE_PRICING_TYPES.map(({ label, value: optionValue }) => {
                return (
                    <FormControlLabel
                        key={optionValue}
                        value={optionValue}
                        control={<Radio />}
                        checked={value === optionValue}
                        label={label}
                        disabled={disabled}
                    />
                );
            })}
        </RadioGroup>
    );
};

const VariableRateFields: FC<{ prefix: string; isDisabled?: boolean }> = ({
    prefix,
    isDisabled,
}) => {
    return (
        <Flex width="100%" justifyContent="space-between">
            {['baseRate', 'lowestRate', 'highestRate'].map((rate) => {
                return (
                    <NPICurrencyInputFieldLabelled
                        key={rate}
                        fieldName={`${prefix}.${rate}`}
                        header={`${rate.replace(/Rate$/, ' Rate')}`}
                        isDisabled={isDisabled}
                    />
                );
            })}
        </Flex>
    );
};

export const TwoToggleRates: FC<TwoToggleRatesProps> = ({
    isOdd,
    namePrefix,
    heading,
    isConstant,
    hardCodedToggle,
}) => {
    const [{ value }, , { setValue }] = useField(`${namePrefix}.type`);
    const isFixed = value === 'FIXED';

    return (
        <Flex
            padding="16px 8px"
            bgcolor={isOdd ? 'rgba(250, 218, 187, 0.15)' : 'transparent'}
            flexDirection="column"
            gap="24px"
        >
            <Flex justifyContent="space-between">
                <Typography fontSize="20px" fontWeight={500} lineHeight="30px" color="#686867">
                    {heading}
                </Typography>
                <PricingTypeToggle
                    value={value}
                    disabled={isConstant || hardCodedToggle}
                    onChange={setValue}
                />
            </Flex>

            <Flex flexDirection="column" width={isFixed ? 'max-content' : '100%'}>
                {isFixed ? (
                    <NPICurrencyInputFieldLabelled
                        fieldName={`${namePrefix}.fixedRate`}
                        header="Fixed Rate"
                        isDisabled={isConstant}
                    />
                ) : (
                    <VariableRateFields prefix={namePrefix} isDisabled={isConstant} />
                )}
            </Flex>
        </Flex>
    );
};
